








































































// vue
import {
    defineComponent,
    onMounted,
    onUnmounted,
    ref,
} from '@vue/composition-api';
import { useStore } from '@/store';

// services
import { useService } from '@/vue3/api/services';

// models
import { GeoRecord } from '@/vue3/api/models';

// hooks
import { useGeoRecord } from '@/vue3/hooks/geoRecordHook';
import { useGeospatial } from '@/vue3/hooks/geospatialHook';
import { useGeometryHoverHook } from '@/vue3/hooks/geometryHoverHook';

// primevue
import Button from 'primevue/button';
import Panel from 'primevue/panel';
import InputSwitch from 'primevue/inputswitch';

// turf
import * as turf from '@turf/turf';

// configs
import geoRecordLayers from '@/vue3/configs/geoRecordLayers';

export default defineComponent({
    components: {
        Button,
        Panel,
        InputSwitch,
    },
    props: {
        mapbox: {
            type: Object,
            default: () => {},
        },
        setMapboxDrawSnap: {
            type: Function,
            default: () => {},            
        },
        clearGeoRecord: {
            type: Function,
            default: () => {},
        },
    },
    setup(props: any) {
        // hooks
        const store = useStore();
        const { translate } = useGeospatial();
        const {
            initDuplicateSource,
            removeDuplicateSource,
            addGeoRecord,
            removeCursorSource,
        } = useGeoRecord();
        const { highlightGeometry, highlightGeoRecordExtrusions } =
            useGeometryHoverHook();

        // services
        const {
            getGeoRecord,
            createGeoRecord,
            getGeoRecordCategoryById,
            getGeoRecordArea,
        } = useService();

        // properties
        const isMeters = store.state.isMeters;

        // refs
        const geoRecord = ref<GeoRecord | null>();
        const cursorLocation = ref([] as Array<number>);
        const centroid = ref<object | undefined>({});

        const isSnappingActivated = ref(false);
        const isGeometrySnappable = ref(false);

        const categoryName = ref('No category');
        const area = ref('');
        const areaUnit = ref('');

        const calculatedArea = ref(0);

        // methods
        const clearGeoRecordSelected = (): void => {
            removeDuplicateSource(props.mapbox.map);
            activateMouseMove();
            activateSimpleSelect();

            geoRecord.value = null;
            isSnappingActivated.value = false;
        };

        const onMouseMove = (e: any): void => {
            if (geoRecord.value) return;

            highlightGeometry(e, props.mapbox.map, 'geo-records');
        };

        const hoverExtrusion = (e: any): void => {
            if (!props.mapbox.map.getLayer('polygon-extrusion')) return;

            highlightGeoRecordExtrusions(
                e,
                props.mapbox.map,
                'polygon-extrusion',
            );
        };

        const activateMouseMove = (): void => {
            props.mapbox.map.on('mousemove', hoverExtrusion);

            geoRecordLayers.forEach(({ id }) => {
                props.mapbox.map.on('mousemove', id, onMouseMove);
                props.mapbox.map.on('mouseleave', id, onMouseMove);
            });
        };

        const deActivateMouseMove = (): void => {
            props.mapbox.map.off('mousemove', hoverExtrusion);

            geoRecordLayers.forEach(({ id }) => {
                props.mapbox.map.off('mousemove', id, onMouseMove);
                props.mapbox.map.off('mouseleave', id, onMouseMove);
            });
        };

        const updateCursorLngLat = (snapFeature: any): Array<number> => {
            return snapFeature.geometry.type === 'LineString'
                ? [
                      snapFeature.geometry.coordinates[0][0],
                      snapFeature.geometry.coordinates[0][1],
                  ]
                : [
                      snapFeature.geometry.coordinates[0][0][0],
                      snapFeature.geometry.coordinates[0][0][1],
                  ];
        };

        const getCentroid = (geometry, geometryType: string): Array<number> => {
            return geometryType === 'LineString'
                ? geometry.coordinates[0]
                : geometry.coordinates[0][0];
        };

        const snapCursor = (feature: any): void => {
            let snapFeature = props.mapbox.draw.getAll().features[0];

            if (!snapFeature) return;

            cursorLocation.value = updateCursorLngLat(snapFeature);
            centroid.value = getCentroid(
                feature.geometry,
                snapFeature.geometry.type,
            );
        };

        const prepareMove = (e: any): any => {
            if (!geoRecord.value) return { source: null };

            const source = props.mapbox.map.getSource('temp-geo-records');

            if (!source) return { source: null };

            let feature = source._data;

            cursorLocation.value = e.lngLat.toArray();

            centroid.value = turf.centroid(feature).geometry?.coordinates;

            if (!centroid.value) return { source: null };

            if (!isSnappingActivated.value) return { source, feature };

            snapCursor(feature);

            return { source, feature };
        };

        const moveGeoRecordLayer = (e: any): void => {
            const { source, feature } = prepareMove(e);

            if (!source || !feature) return;

            source.setData(
                translate(
                    feature,
                    centroid.value as number[],
                    cursorLocation.value,
                ),
            );
        };

        const getGeometryArea = async (): Promise<void> => {
            if (!geoRecord.value) return;

            calculatedArea.value = await getGeoRecordArea(geoRecord.value.id);

            area.value = (
                calculatedArea.value * (isMeters ? 1 : 1.195990046301)
            ).toFixed(2);

            isMeters ? (areaUnit.value = 'm²') : (areaUnit.value = 'yd²');
        };

        const assignCateroryName = async (): Promise<void> => {
            if (!geoRecord.value?.geoRecordCategoryId) return;

            categoryName.value = (
                await getGeoRecordCategoryById(
                    geoRecord.value.geoRecordCategoryId,
                )
            ).name;
        };

        const toggleSnapButtonVisibility = (): boolean => {
            return geoRecord.value?.geometry.type === 'Polygon' ||
                geoRecord.value?.geometry.type === 'LineString'
                ? true
                : false;
        };

        const initDuplication = async (e): Promise<void> => {
            const feature = props.mapbox.map.queryRenderedFeatures(e.point);

            if (feature.length === 0) return;

            geoRecord.value = await getGeoRecord(feature[0].id);

            isGeometrySnappable.value = toggleSnapButtonVisibility();

            await assignCateroryName();
            getGeometryArea();
            initDuplicateSource(props.mapbox.map, geoRecord.value);
            deActivateMouseMove();
        };

        const activateSimpleSelect = (): void => {
            props.mapbox.map.getCanvas().style.cursor = '';
            props.mapbox.draw.changeMode('simple_select');
            removeCursorSource(props.mapbox.map);
        };

        const activateSnapping = (): void => {
            if (!isSnappingActivated.value) return;

            geoRecord?.value?.geometry.type === 'Polygon'
                ? props.mapbox.draw.changeMode('SnapPolygonMode')
                : props.mapbox.draw.changeMode('SnapLineStringMode');

            // props.mapbox.draw.options.snap = isSnappingActivated.value;
            props.setMapboxDrawSnap(isSnappingActivated.value)
        };

        const onMapClick = async (e: any): Promise<void> => {
            activateSimpleSelect();
            activateSnapping();

            if (!geoRecord.value) return initDuplication(e);

            geoRecord.value.accountId = store.getters.account.id;

            const newGeoRecord = await createGeoRecord(geoRecord.value);

            addGeoRecord(props.mapbox.map, newGeoRecord);
        };

        const activateDuplicateTool = (): void => {
            activateMouseMove();

            props.mapbox.map.on('click', onMapClick);
            props.mapbox.map.on('mousemove', moveGeoRecordLayer);
        };

        const deActivateDuplicateTool = (): void => {
            clearGeoRecordSelected();
            deActivateMouseMove();

            props.mapbox.map.off('click', onMapClick);
            props.mapbox.map.off('mousemove', moveGeoRecordLayer);
        };

        // lifecycle
        onMounted(() => {
            activateDuplicateTool();
        });

        onUnmounted(() => {
            deActivateDuplicateTool();
        });

        return {
            area,
            areaUnit,
            geoRecord,
            categoryName,
            isSnappingActivated,
            isGeometrySnappable,
            clearGeoRecordSelected,
            activateSnapping,
        };
    },
});
